export type Theme = "system" | "light" | "dark";

const KEY = "rate2728.theme";

const mq = () =>
  typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-color-scheme: dark)")
    : null;

/** What "system" currently resolves to. */
export function resolve(t: Theme): "light" | "dark" {
  if (t !== "system") return t;
  return mq()?.matches ? "dark" : "light";
}

/**
 * The adminHMD stylesheet treats "no data-theme" as light, so system mode has
 * to be written out explicitly rather than left unset. Both attributes are set
 * because the theme keys off data-theme and Bootstrap off data-bs-theme.
 */
export function applyTheme(t: Theme): void {
  const root = document.documentElement;
  const actual = resolve(t);
  root.setAttribute("data-theme", actual);
  root.setAttribute("data-bs-theme", actual);
  try {
    localStorage.setItem(KEY, t);
  } catch { /* private mode: the choice just will not persist */ }
}

/**
 * Light is the default, not the OS setting: this dashboard is read next to the
 * Masterfile in Excel and next to the other Happy Trails tools, all of which
 * are light, and a reader whose laptop happens to be in dark mode should not
 * get an odd one out. "System" is still one step away on the toggle for anyone
 * who wants it, and it is remembered once chosen.
 *
 * It also removes the first-paint flash for everyone who never chooses: the
 * theme is applied from an effect, and the stylesheet reads "no data-theme"
 * as light, so the default now matches what the page shows before React runs.
 */
export function readTheme(): Theme {
  try {
    const v = localStorage.getItem(KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch { /* unreadable storage falls back to the default below */ }
  return "light";
}

/** Re-apply on OS change while the user is on "system". Returns an unsubscribe. */
export function watchSystem(onChange: () => void): () => void {
  const m = mq();
  if (!m) return () => {};
  m.addEventListener("change", onChange);
  return () => m.removeEventListener("change", onChange);
}

export const NEXT_THEME: Record<Theme, Theme> = {
  system: "light",
  light: "dark",
  dark: "system",
};

export const THEME_LABEL: Record<Theme, string> = {
  system: "System theme",
  light: "Light theme",
  dark: "Dark theme",
};

/** Bootstrap Icons class per mode. */
export const THEME_ICON: Record<Theme, string> = {
  system: "bi bi-circle-half",
  light: "bi bi-sun",
  dark: "bi bi-moon-stars",
};
